class BrandInstructions extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-brand-trigger]');
    this.triggerLabel = this.querySelector('[data-brand-trigger-label]');
    this.menu = this.querySelector('[data-brand-menu]');
    this.panelsContainer = this.querySelector('[data-brand-panels]');
    if (!this.trigger || !this.menu || !this.panelsContainer) return;

    this.panels = this.panelsContainer.querySelectorAll('[data-brand-panel]');
    if (this.panels.length === 0) return;

    this.langButtons = this.querySelectorAll('[data-brand-lang]');
    this.currentLang = 'es';

    this.buildMenu();
    this.options = this.menu.querySelectorAll('[data-brand-option]');

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onOptionClick = this.onOptionClick.bind(this);
    this.onOptionKey = this.onOptionKey.bind(this);
    this.onDocClick = this.onDocClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onLangClick = this.onLangClick.bind(this);

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.options.forEach((opt) => {
      opt.addEventListener('click', this.onOptionClick);
      opt.addEventListener('keydown', this.onOptionKey);
    });
    this.langButtons.forEach((btn) => btn.addEventListener('click', this.onLangClick));
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKeyDown);

    this.applyLang(this.currentLang);

    const initial = this.panels[0]?.dataset.brandId;
    if (initial) this.select(initial);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKeyDown);
  }

  buildMenu() {
    const items = Array.from(this.panels).map((panel) => {
      const id = panel.dataset.brandId || '';
      const label = panel.dataset.brandLabel || '';
      const color = panel.dataset.brandColor || '#000000';
      return `<li class="brand-instructions__option" role="option" data-brand-option data-value="${this.escape(id)}" data-color="${this.escape(color)}" style="color: ${this.escape(color)};" tabindex="0">${this.escape(label)}</li>`;
    });
    this.menu.innerHTML = items.join('');
  }

  escape(str) {
    return String(str).replace(/[&<>"']/g, (ch) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch])
    );
  }

  open() {
    this.menu.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.menu.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  toggle() {
    if (this.menu.hidden) this.open();
    else this.close();
  }

  onTriggerClick(event) {
    event.stopPropagation();
    this.toggle();
  }

  onOptionClick(event) {
    const value = event.currentTarget.dataset.value;
    this.select(value);
    this.close();
  }

  onOptionKey(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const value = event.currentTarget.dataset.value;
      this.select(value);
      this.close();
      this.trigger.focus();
    }
  }

  onDocClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && !this.menu.hidden) {
      this.close();
      this.trigger.focus();
    }
  }

  select(value) {
    let label = '';
    let color = '';
    this.options.forEach((opt) => {
      const isMatch = opt.dataset.value === value;
      opt.setAttribute('aria-selected', isMatch ? 'true' : 'false');
      if (isMatch) {
        label = opt.textContent.trim();
        color = opt.dataset.color || '';
      }
    });
    if (this.triggerLabel && label) this.triggerLabel.textContent = label;
    if (color) this.trigger.style.color = color;

    this.panels.forEach((panel) => {
      if (panel.dataset.brandId === value) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    });
  }

  onLangClick(event) {
    const lang = event.currentTarget.dataset.brandLang;
    if (!lang) return;
    this.currentLang = lang;
    this.langButtons.forEach((btn) =>
      btn.setAttribute('aria-selected', btn.dataset.brandLang === lang ? 'true' : 'false')
    );
    this.applyLang(lang);
  }

  applyLang(lang) {
    // For each brand panel, show the content div for the chosen lang.
    // If that lang's content is empty, fall back to the ES content.
    this.panels.forEach((panel) => {
      const contents = panel.querySelectorAll('[data-lang]');
      const wanted = panel.querySelector(`[data-lang="${lang}"]`);
      const isEmpty = !wanted || !wanted.textContent.trim();
      const showLang = isEmpty ? 'es' : lang;
      contents.forEach((c) => {
        if (c.dataset.lang === showLang) c.removeAttribute('hidden');
        else c.setAttribute('hidden', '');
      });
    });
  }
}

if (!customElements.get('brand-instructions')) {
  customElements.define('brand-instructions', BrandInstructions);
}
